// "Run the skill" — the copyable Cowork text for this practice.
//
// The words are Instance C's (skillText.js mirrors SKILL.md's "How to run"); this
// component fills in the practice id, the portal key and Atlas's base URL, and
// gives a Copy button. It never shows, asks for, or embeds an API key — only
// where one comes from and where it goes.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchRunbook } from "./api";
import SkillBundlePanel from "./SkillBundlePanel";
import { SKILL_TEXT_SOURCE, fillSkillText } from "./skillText";
import "./Queue.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export default function SkillPage({ practiceId, isAdmin = false }) {
  const [portals, setPortals] = useState(null);
  const [portal, setPortal] = useState("");
  const [error, setError] = useState(null);
  const [copyState, setCopyState] = useState(null); // 'copied' | 'failed'
  const preRef = useRef(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId) return;
    const mine = (ticket.current += 1);
    setError(null);
    try {
      const body = await fetchRunbook(practiceId);
      if (mine !== ticket.current) return;
      setPortals(body.portals);
      setPortal(
        body.portal || (body.portals.length === 1 ? body.portals[0] : ""),
      );
    } catch (err) {
      if (mine !== ticket.current) return;
      // The text is still useful without a portal key; say what is missing.
      setError(`${err.message} The portal key below is left as a marker.`);
      setPortals([]);
    }
  }, [practiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const baseUrl = API_URL || window.location.origin;
  const text = fillSkillText({ practiceId, portalKey: portal, baseUrl });

  const copy = async () => {
    setCopyState(null);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState("copied");
    } catch (err) {
      // Clipboard can be refused (permissions, an insecure origin). Select the text
      // so a manual copy is one keystroke away, and say so.
      const range = document.createRange();
      if (preRef.current) {
        range.selectNodeContents(preRef.current);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
      setCopyState("failed");
    }
  };

  return (
    <div className="sq-skill">
      {/* First, because handing the work to somebody else is the thing an admin comes
          to this tab to do; the copyable text below is for running it here. */}
      <SkillBundlePanel practiceId={practiceId} isAdmin={isAdmin} />

      <p className="sq-sub">
        Paste this into Cowork on the machine whose Chrome is logged in to the
        portal. It runs against practice <strong>{practiceId}</strong>.
      </p>

      <div className="sq-run-banner grey">
        <span>
          The skill needs a <code>queue:work</code> API key for this practice in{" "}
          <code>ATLAS_API_KEY</code>. An Atlas admin issues it on{" "}
          <strong>Practice Details → API keys</strong>, where it is shown once.
          The key goes into the environment on that machine — never into chat, a
          command line, or this page.
        </span>
      </div>

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {portals && portals.length === 0 && !error && (
        <div className="sq-message warn">
          <span>
            No runbook yet, so there is no portal key to fill in. The commands
            below keep a &lt;portal key&gt; marker until the first runbook is
            posted.
          </span>
        </div>
      )}

      <div className="sq-tab-toolbar">
        {portals && portals.length > 1 ? (
          <label className="sq-inline-field">
            Portal
            <select value={portal} onChange={(e) => setPortal(e.target.value)}>
              <option value="">Choose a portal…</option>
              {portals.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="sq-muted">{portal ? `Portal: ${portal}` : ""}</span>
        )}
        <span className="sq-skill-copy">
          {copyState === "copied" && <span className="sq-muted">Copied.</span>}
          {copyState === "failed" && (
            <span className="sq-muted">
              Copy was blocked — the text is selected; copy it with ⌘C or
              Ctrl+C.
            </span>
          )}
          <button className="btn btn-primary btn-sm" onClick={copy}>
            Copy
          </button>
        </span>
      </div>

      <pre className="sq-skill-text" ref={preRef}>
        {text}
      </pre>

      <p className="sq-muted">
        Text from {SKILL_TEXT_SOURCE.repo} {SKILL_TEXT_SOURCE.path} (“
        {SKILL_TEXT_SOURCE.section}”) at {SKILL_TEXT_SOURCE.commit}.
      </p>
    </div>
  );
}
